import React, { useState } from 'react';
import styled from 'styled-components';
import Column from '../general/Column';
import Row from '../general/Row';
import RowTwo from '../general/RowTwo';
import Loader from '../general/Loader';
import useBrand from '../../hooks/useBrand';

const StyledDistributors = styled.div`
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin-right: auto;
  margin-left: auto;
`;

const StyledRow = styled.div<{ $brand?: string }>`
  width: 100%;
  display: grid;
  gap: 16px;
  margin-right: -15px;
  margin-left: -15px; 
  justify-content: center;
  grid-template-columns: 1fr;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }

  ${({ $brand }) => $brand == 'jackson' && `
    background: #F1F1F1;
    width: 100vw;
    position: relative;
    padding: 24px;
    
    @min-width (min-width: 1024px) {
      padding: 64px;
    }
  `}
`;

const StyledContinent = styled.div<{ $brand?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 274px;
  text-align: center;
  border-radius: 16px;
  background: #F5F5F5;
  flex-grow: 1;
  width: 100%;
  padding: 15px;

  h5 {
    font-size: 24px;
    color: #000;
    margin-bottom: .5rem;
    line-height: 1.2;
    margin-top: 0;
  }
  
  .view-link {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 17px;
    padding: 10px 24px;
    border-radius: 24px;
    border: 1px solid #1A1B1B;

    &:hover {
        font-weight: 600;
        margin-top: 16px;
    }

    a {
      text-decoration: none;
    }
  }

  ${({ $brand }) => $brand == 'jackson' && `
    border-radius: 0;
    background: #FFF;

    .view-link {
      border-radius: 0;

      &:hover {
        margin-top: 17px;
      }

      a {
        text-transform: uppercase;
        font-weight: 600;
      }
    }
  `}
`;

const StyledReturnButton = styled.div<{ $brand?: string }>`
  margin-top: 25px;
  text-align: center;
  justify-content: center;
  display: flex;

  a {
    border-radius: 100px;
    font-size: 14px;
    transition: all .3s ease-in-out;
    border: 1px solid #000;
    padding: 10px 24px;
    line-height: 1em;
    cursor: pointer;
    border-style: solid;
    text-align: center;
    color: #1A1B1B;
    font-size: 16px;
    font-style: normal;
    line-height: 20px;
  }

  a:hover {
    background: #000;
    color: #fff;
  }

  ${({ $brand }) => $brand == 'jackson' && `
    background: #F1F1F1;
    width: 100vw;
    margin-top: 0px;
    padding: 64px;

    a {
      border-radius: 0;
    }
  `}
`;

const StyledDistributorGrid = styled.div<{ $brand?: string }>`
  width: 100%;
  display: grid;
  gap: 16px;
  justify-content: center;
  grid-template-columns: 1fr;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  ${({ $brand }) => $brand == 'jackson' && `
    background-color: #F1F1F1;
    width: 100vw;
    padding: 0 15px;
    display: grid;
    justify-content: center;
    margin-top: 32px;
  `}
`;

const StyledDistributorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  height: 274px;
  text-align: left;
  flex-grow: 1;
  width: 100%;
  padding: 15px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;

  .country {
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.33;
    letter-spacing: .5px;
    color: #888;
  }

  .name {
    color: #1A1B1B;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    margin-bottom: 8px;
  }

  .address, .phone {
    color: #1A1B1B;
    font-size: 16px;
    font-style: normal;
    line-height: 24px;
  }
`;

const SubContainer = styled.div<{ $brand?: string }>`
  width: 100%;
  display: flex;
  gap: 16px;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin-bottom: 20px;

  ${({ $brand }) => $brand == 'jackson' && `
    gap: 0;
    margin-bottom: 0;
  `}
`;

const HTag = styled.div<{ $brand?: string }>`
  color: #000;
  text-align: center;
  font-size: ${({ $brand }) => ($brand == 'jackson' ? '32px' : '24px')};
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
`;

const PTag = styled.div<{ $brand?: string }>`
  color: #1A1B1B;
  text-align: center;
  font-size: 14px;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.14px;
  margin-bottom: 28px;
  max-width: 450px;

  ${({ $brand }) => $brand == 'jackson' && `
    margin-top: 16px;
  `}
`;

interface Props {
  site: string | null;
  country: string;
  brandKey: string;
}

const Distributors: React.FC<Props> = ({ site, country, brandKey }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [results, setResults] = useState<any>([]);
  const [data, setData] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [showDistributors, setShowDistributors] = useState<boolean>(true);
  const [currentContinent, setCurrentContinent] = useState<string>('');
  const brand = useBrand();

  let distributorUrl: string;
  const dealerLocatorReact = document.getElementById('dealer-locator-react');

  if (dealerLocatorReact !== null) {
    distributorUrl = dealerLocatorReact.getAttribute('data-distributor-url') as string;
  }

  const getDistributors = (continent: string) => {
    setLoading(true);

    let data = {
      "Brand": site,
      "continent": continent,
    }
  
    let apiUrl = distributorUrl;

    setData(JSON.stringify(data));
  //  console.log('post data', data);

    const postData = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    };

    fetch(apiUrl, postData)
      .then((res) => res.json())
      .then((data) => {
        console.log('response data', data);
        if (data.data && data.data.continents && data.data.continents[continent]) {
          setResults(data.data.continents[continent]);
          setError(false);
          setErrorMsg('');
          setLoading(false);
          return;
        } else {
          setError(true);
          setResults([]);
          setLoading(false);
          setErrorMsg('No results found');
          return;
        }
      })
      .catch((error) => {
        console.log('error', JSON.stringify(error));
        if (error.response) {
          console.log('error.response', error.response);
          console.log('error.response.data', error.response.data);
          console.log('error.response.status', error.response.status);
          console.log('error.response.headers', error.response.headers);
          setErrorMsg(error.response);
        }
        if (error.request) {
          console.log('error.request', error.request);
        }
        if (error.message) {
          console.log('error.message', error.message);
        }
        if (error.config) {
          console.log('error.config', error.config);
        }
        if (!errorMsg || errorMsg === '') {
          setErrorMsg(error);
        }
        setError(true);
        setResults([]);
        setLoading(false);
        return;
      });

    setShowDistributors(false);
  };

  const handleContinentClick = (continent: string, event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    console.log(continent);
    setCurrentContinent(continent);
    var continentID = continent.toUpperCase().replace(/ /g, '_');
    getDistributors(continentID);
  };

  interface ContinentProps {
    continent: string;
    handleContinentClick: any;
    position: number;
  }

  const Continent = (props: ContinentProps) => {
    return (
      <StyledContinent $brand={brand} onClick={(event) => props.handleContinentClick(props.continent, event)}>
        <h5>{props.continent}</h5>
        <div data-position={props.position + 1} data-continent={props.continent} data-distributor-clicked className="view-link">
          <a href="#0" className="btn btn-block btn-primary">
            View Region
          </a>
        </div>
      </StyledContinent>
    );
  };

  const ReturnButton = () => {
    return (
      <StyledReturnButton className="return-link" $brand={brand}>
        <a
          href="#0"
          className="btn btn-block btn-primary return-button"
          onClick={(event) => setShowDistributors(true)}
        >
          Return to Regions
        </a>
      </StyledReturnButton>
    );
  };

  const continents = ['Africa', 'Asia', 'Europe', 'Middle East', 'North America', 'Oceania', 'South America'].map(
    (continent, index) => {
      return <Continent position={index} key={index} continent={continent} handleContinentClick={handleContinentClick} />;
    }
  );

  interface Props {
    site: string | null;
    country: string;
    brandKey: string;
  }

  let distributors = [];

  if (results && results.distributors && results.distributors.length > 0) {
    distributors = results.distributors.map((result: any, index: any) => (
      <StyledDistributorContainer key={index}>
        <div className="distributor">
          <div className="country">{result.country}</div>
          <div className="name">{result.distributor.name}</div>
          <div className="address">{result.distributor.address}</div>
          <div className="email">{result.distributor.email}</div>
          <div className="phone">{result.distributor.phone}</div>
        </div>
      </StyledDistributorContainer>
    ));
  }

  return (
    <StyledDistributors>
      <Row>
        <Column>
          {!showDistributors && loading && <Loader />}
          {(showDistributors && (
            <SubContainer $brand={brand}>
              <HTag $brand={brand} >Locate a {site} Authorized Distributor</HTag>
              <PTag $brand={brand}>
                Our products are available for purchase from a worldwide network of authorized dealers. Please contact
                your local distributor (listed below) to find a dealer near you.
              </PTag>
              <StyledRow $brand={brand}>{continents}</StyledRow>
            </SubContainer>
          )) ||
            (error && !loading && (
              <SubContainer $brand={brand}>
                <HTag $brand={brand}>{currentContinent}</HTag>
                <RowTwo>No Results</RowTwo>
                <ReturnButton />
              </SubContainer>
            )) ||
            (results && distributors && !loading && (
              <SubContainer $brand={brand}>
                <HTag $brand={brand}>{currentContinent}</HTag>
                <StyledDistributorGrid $brand={brand}>{distributors}</StyledDistributorGrid>
                <ReturnButton />
              </SubContainer>
            ))}
        </Column>
      </Row>
    </StyledDistributors>
  );
};

export default Distributors;
